import { Image } from "@chakra-ui/image"
import { Box, Text, Flex } from "@chakra-ui/layout"

import { UserAvatar } from "../../common/avatar"
import ShowDateOverlay from "./show-date-overlay"
import ShowLiveOverlay from "./show-live-overlay"
import { checkIsTouchDevice } from "../../../util"
import ShowCardHoverOverlay from "./show-card-hover-overlay"
import { ShowRoom } from "../../../recoil/atoms/shows/types"
import ShowCardLoaderOverlay from "./show-card-loader-overlay"
import useOnClickWithTouchHandler from "../../../hooks/useOnClickWithTouchHandler"

export type ShowCardProps = ShowRoom & {
  openShow: (roomHash: string) => void
  openShowInNewTab: (roomHash: string) => void
  loadingRoomhash: string | null
  listLength: number
}

const ShowCard = ({
  name,
  isLive,
  branding,
  thumbnailUrl,
  openShow,
  loadingRoomhash,
  listLength,
  ...props
}: ShowCardProps) => {
  const roomHash =
    props.moderatorHash ?? props.broadcasterHash ?? props.audienceHash

  const isCurrentRoomLoading = loadingRoomhash === roomHash
  const isTouchDevice = checkIsTouchDevice()

  // don't show the hover overlay if touch device or the other room is joining
  const hideHoverOverlay =
    isTouchDevice || (loadingRoomhash && !isCurrentRoomLoading)

  // handle card on click
  const handleOnClick = () => {
    !loadingRoomhash && roomHash && openShow(roomHash)
  }

  const openShowInNewTab = () => {
    roomHash && props.openShowInNewTab(roomHash)
  }

  const { onTouchEnd, onTouchStart } = useOnClickWithTouchHandler(handleOnClick)

  return (
    <Box
      pos="relative"
      borderRadius="8px"
      overflow="hidden"
      outline="none"
      // width="240px"
      width={listLength === 1 ? "240px" : ["170px", "200px", "240px"]}
    >
      <Flex
        width="100%"
        flexDir="column"
        padding="8px"
        backgroundColor={branding.joinPagePanelBackgroundColor ?? "gray.200"}
        borderWidth="1px"
        borderColor={branding?.joinPagePanelBorderColor ?? "transparent"}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <Flex alignItems="center">
          <UserAvatar
            name={"avatar-name"}
            size={"xs"}
            src={branding.roomAvatarImage}
            colorDisk={"transparent"}
            avatar={null}
            avatarStyle={{ width: "1rem", height: "1rem" }}
            fontSize="0.6rem"
          />
          <Text
            fontSize="0.8rem"
            marginInlineStart="0.3rem"
            fontWeight="500"
            noOfLines={1}
            color={branding.joinPagePanelTextColor ?? "white"}
          >
            {name}
          </Text>
        </Flex>

        <Flex
          grow={1}
          marginTop="1.5"
          overflow="hidden"
          borderRadius="4px"
          style={{ aspectRatio: "9/16" }}
          position="relative"
          role="group" // added group for applying _groupHover to child
        >
          <Image objectFit="contain" src={thumbnailUrl} alt="Chakra UI" />

          {/* Overlay */}
          <Box position="absolute" zIndex="2" padding="2">
            {/* LIVE */}
            {isLive && <ShowLiveOverlay />}

            {/* Date */}
            {!isLive && props.localCountdownTime && (
              <ShowDateOverlay date={props.localCountdownTime} />
            )}
          </Box>

          {isCurrentRoomLoading && isTouchDevice && <ShowCardLoaderOverlay />}

          <Box
            display={isCurrentRoomLoading && !isTouchDevice ? "flex" : "none"}
            position="absolute"
            zIndex="4"
            top="0"
            left="0"
            width="100%"
            height="100%"
            _groupHover={{
              display: hideHoverOverlay ? "none" : "flex"
            }}
          >
            <ShowCardHoverOverlay
              branding={branding}
              openShow={handleOnClick}
              openShowInNewTab={openShowInNewTab}
              isRoomloading={!!loadingRoomhash}
              isCurrentRoomLoading={isCurrentRoomLoading}
            />
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

export default ShowCard
